<template>
  <div class="yt-facade relative w-full overflow-hidden rounded-xl bg-black" :class="[aspectClass, theme, { 'is-playing': state === 'PLAYING' }]">
    <!-- Facade レイヤー（初期表示。iframe は未生成） -->
    <template v-if="state !== 'PLAYING'">
      <img v-if="thumbSrc" alt="" class="yt-facade__thumb absolute inset-0 size-full object-cover" decoding="async" loading="lazy" :src="thumbSrc" @error="onThumbError" />
      <div v-else class="yt-facade__placeholder absolute inset-0 flex flex-col items-center justify-center gap-2">
        <span class="text-3xl opacity-20">▶</span>
        <span class="text-[11px] tracking-[0.18em] text-white/20">YOUTUBE</span>
      </div>

      <div class="yt-facade__overlay absolute inset-0" />
      <div class="yt-facade__accent absolute left-0 top-0 h-full w-[3px]" />

      <button class="yt-facade__play absolute inset-0 flex items-center justify-center" type="button" :aria-label="playLabel" :disabled="state === 'LOADING'" @click="handlePlay">
        <span v-if="state === 'FACADE'" class="yt-facade__play-btn flex items-center justify-center rounded-full">
          <span class="yt-facade__play-tri" />
        </span>
        <span v-else class="yt-facade__ring" />
      </button>

      <p v-if="props.showOverlayTitle && displayTitle" class="yt-facade__title absolute bottom-[18px] left-5 right-5">{{ displayTitle }}</p>
    </template>

    <!-- 再生レイヤー（クリック後に動的注入。nocookie ドメイン） -->
    <iframe
      v-if="state !== 'FACADE'"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      class="yt-facade__iframe absolute inset-0 size-full border-0"
      :class="{ 'is-visible': state === 'PLAYING' }"
      :src="iframeSrc"
      :title="displayTitle || 'YouTube video player'"
      allowfullscreen
      @load="onIframeLoad"
    />
  </div>
</template>

<script setup lang="ts">
import { useYouTubeTitle } from '@/wabunka-src/common/features/Featured/composables/query/useYouTubeTitle';
import { buildEmbedSrc, buildThumbnailUrl } from '@/wabunka-src/common/features/Featured/core/youtubeRichText';
import { ThemeKey } from '@/wabunka-src/common/features/Featured/injectionKeys';

const props = withDefaults(
  defineProps<{
    videoId: string;
    /**
     * タイトルの静的上書き（任意）。通常は指定せず、videoId からプロキシ経由で自動取得する。
     * テストで決定的に表示したい場合や、呼び出し側が既に取得済みの値を持つ場合に指定する。
     */
    title?: string;
    /**
     * サムネイル上に重ねるタイトル（hover でフェードイン）を描画するか。
     * 呼び出し側が facade の外に同じタイトルを常時表示する場合、
     * PC の hover 時に二重表示になるため false を渡す（JCAT-5830）。
     */
    showOverlayTitle?: boolean;
    onPlaying?: () => void;
  }>(),
  {
    title: undefined,
    // 既定は従来どおり描画する。旅程ページ等の既存呼び出しは無変更で挙動が変わらない。
    showOverlayTitle: true,
    onPlaying: undefined,
  },
);

// テーマは props で受け取らず inject で取得する（StyledRichText と同一。未 provide 時は 'light'）。
const theme = inject(ThemeKey, ref('light'));

// タイトルは videoId からクライアントで遅延取得する（title prop 指定時は取得をスキップしそちらを優先）。
const { title: fetchedTitle } = useYouTubeTitle(() => (props.title ? undefined : props.videoId));
const displayTitle = computed(() => props.title ?? fetchedTitle.value);

type FacadeState = 'FACADE' | 'LOADING' | 'PLAYING';
const state = ref<FacadeState>('FACADE');

// サムネフォールバック: false(maxres) → 'max'(hq) → 'all'(プレースホルダ)
const thumbError = ref<false | 'max' | 'all'>(false);

const thumbSrc = computed(() => {
  if (thumbError.value === 'all') return undefined;
  return buildThumbnailUrl(thumbError.value === 'max' ? 'hqdefault' : 'maxresdefault', props.videoId);
});

// iOS（iPadOS の desktop UA も maxTouchPoints で判定）。autoplay 制限は iOS の WebKit 全ブラウザ共通。
const isIos = () => {
  if (typeof navigator === 'undefined') return false;
  return /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// iOS は未ミュートだと autoplay がブロックされ初回タップで再生されない（プレーヤー一時停止表示）。
// iOS のみ mute=1 を付けて 1 タップ再生にする（Desktop/Android は音ありのまま）。
const iframeSrc = computed(() => buildEmbedSrc(props.videoId, { muted: isIos() }));

// YouTube は 16:9 が標準。全ブレークポイント共通（従来の MicroCMS 埋め込み＝padding-bottom:56.25% と同じ比率）。
const aspectClass = 'aspect-[16/9]';

const playLabel = computed(() => (displayTitle.value ? `Play video: ${displayTitle.value}` : 'Play video'));

const onThumbError = () => {
  if (thumbError.value === false) thumbError.value = 'max';
  else if (thumbError.value === 'max') thumbError.value = 'all';
};

// iframe の load イベントが来ない場合（CSP の frame-src ブロック・ネットワーク失敗等。
// cross-origin iframe では @error が発火しないことがある）に LOADING が固着して
// 操作不能になるのを防ぐためのタイムアウト。期限内に PLAYING へ遷移しなければ FACADE に戻す。
const LOADING_TIMEOUT_MS = 8000;
let loadingTimer: ReturnType<typeof setTimeout> | undefined;

const clearLoadingTimer = () => {
  if (loadingTimer) {
    clearTimeout(loadingTimer);
    loadingTimer = undefined;
  }
};

const handlePlay = () => {
  // ローディング中・再生済みの重複クリックをガードする。
  if (state.value !== 'FACADE') return;
  state.value = 'LOADING';
  clearLoadingTimer();
  loadingTimer = setTimeout(() => {
    if (state.value === 'LOADING') state.value = 'FACADE'; // 再クリックで再試行可能に戻す
  }, LOADING_TIMEOUT_MS);
};

const onIframeLoad = () => {
  if (state.value === 'LOADING') {
    state.value = 'PLAYING';
    props.onPlaying?.();
  }
  clearLoadingTimer();
};

onBeforeUnmount(clearLoadingTimer);
</script>

<style lang="scss" scoped>
.yt-facade {
  cursor: pointer;

  &.is-playing {
    cursor: default;
  }
}

.yt-facade__thumb {
  filter: brightness(0.82) saturate(0.9);
  transition:
    transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    filter 0.5s ease;
}

.yt-facade:hover .yt-facade__thumb {
  transform: scale(1.04);
  filter: brightness(0.65) saturate(0.85);
}

.yt-facade__placeholder {
  background: linear-gradient(135deg, #2a2520, #1a1510);
}

.yt-facade__overlay {
  background: linear-gradient(160deg, transparent 30%, rgba(26, 24, 20, 0.6) 100%);
  pointer-events: none;
}

.yt-facade__accent {
  background: linear-gradient(to bottom, #b8965a, transparent 70%);
  opacity: 0;
  transition: opacity 0.4s;
}

.yt-facade:hover .yt-facade__accent {
  opacity: 1;
}

.yt-facade__play {
  background: transparent;
  border: 0;
  cursor: inherit;
}

.yt-facade__play-btn {
  width: 64px;
  height: 64px;
  border: 1.5px solid rgba(212, 176, 122, 0.65);
  background: rgba(26, 24, 20, 0.3);
  backdrop-filter: blur(6px);
  transition: all 0.35s ease;
}

.yt-facade:hover .yt-facade__play-btn {
  border-color: #b8965a;
  background: rgba(26, 24, 20, 0.55);
  transform: scale(1.1);
  box-shadow: 0 0 24px rgba(184, 150, 90, 0.25);
}

.yt-facade__play-tri {
  width: 0;
  height: 0;
  margin-left: 4px;
  border-style: solid;
  border-width: 9px 0 9px 17px;
  border-color: transparent transparent transparent #d4b07a;
  transition: border-color 0.35s;
}

.yt-facade:hover .yt-facade__play-tri {
  border-color: transparent transparent transparent #b8965a;
}

.yt-facade__ring {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1.5px solid transparent;
  border-top-color: #b8965a;
  animation: yt-facade-spin 0.8s linear infinite;
}

@keyframes yt-facade-spin {
  to {
    transform: rotate(360deg);
  }
}

.yt-facade__title {
  font-family: Georgia, serif;
  font-size: 15px;
  line-height: 1.45;
  letter-spacing: 0.03em;
  color: #fff;
  text-shadow: 0 1px 10px rgba(0, 0, 0, 0.6);
  opacity: 0;
  transform: translateY(6px);
  transition:
    opacity 0.4s,
    transform 0.4s;
  pointer-events: none;
}

.yt-facade:hover .yt-facade__title {
  opacity: 1;
  transform: translateY(0);
}

.yt-facade__iframe {
  opacity: 0;
  transition: opacity 0.3s ease;

  &.is-visible {
    opacity: 1;
  }
}
</style>
